import { FC, useCallback } from 'react';
import { InlineLoading, Tag } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import 'components/inline-edit/ibm-products-config';
import { Section } from './section';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatNumber } from 'utils/number';
import { fetchRunDetail } from './api';
import { useAgentResource } from './use-agent-resource';
import { formatRunCost, statusTagColor } from '../format';
import type { AgentRun } from './types';
import styles from './page.module.scss';

interface RunDetailSidePanelProps {
  run: AgentRun | null;
  open: boolean;
  onClose: () => void;
}

const formatInt = (value: number): string =>
  formatNumber({ value, minPrecision: 0, includeCommas: true });

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

/**
 * A tool call's outcome. A call with no recorded outcome (refused past its
 * cap, or never matched to its execution) is neither OK nor Failed.
 * @param props The call's outcome.
 * @param props.success Whether the call succeeded, or null when unknown.
 * @return The outcome tag.
 */
function ToolOutcomeTag({ success }: { success: boolean | null }) {
  if (success === null) {
    return (
      <Tag type="gray" size="sm">
        {EMPTY_CELL}
      </Tag>
    );
  }
  return (
    <Tag type={success ? 'green' : 'red'} size="sm">
      {success ? 'OK' : 'Failed'}
    </Tag>
  );
}

export const RunDetailSidePanel: FC<RunDetailSidePanelProps> = ({
  run,
  open,
  onClose,
}) => {
  const runId = run?.id;

  const {
    data: detail,
    loading,
    error,
  } = useAgentResource(
    useCallback(
      (controller: AbortController) =>
        fetchRunDetail(runId as number, controller),
      [runId]
    ),
    [runId],
    open && runId !== undefined
  );

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={run?.runFor || `Run #${run?.id ?? ''}`}
      subtitle={
        run
          ? `Run #${run.id} · ${run.model ?? 'unknown model'} · ${formatRunCost(
              run.cost
            )}`
          : undefined
      }
      placement="right"
      size="lg"
      slideIn
      selectorPageContent="#main-content"
    >
      {run && (
        <div className={styles.detailBody}>
          <div className={styles.summaryRow}>
            <Tag type={statusTagColor(run.status)} size="sm">
              {run.status}
            </Tag>
            <span className={styles.summaryMeta}>
              {formatInt(run.tokens.total)} tokens
            </span>
            <span className={styles.summaryMeta}>
              {formatRunCost(run.cost)}
            </span>
            {detail?.unpriced && (
              <Tag type="magenta" size="sm" title="Model not in the price list">
                Unpriced
              </Tag>
            )}
          </div>

          <Section title="Tokens">
            <div className={styles.tokenGrid}>
              <span>Input</span>
              <span>{formatInt(run.tokens.input)}</span>
              <span>Output</span>
              <span>{formatInt(run.tokens.output)}</span>
              <span>Cache write</span>
              <span>{formatInt(run.tokens.cacheWrite)}</span>
              <span>Cache read</span>
              <span>{formatInt(run.tokens.cacheRead)}</span>
            </div>
          </Section>

          {run.error && (
            <Section title="Error" defaultExpanded>
              <div className={styles.detailError}>{run.error}</div>
            </Section>
          )}

          {loading && <InlineLoading description="Loading run detail..." />}
          {error && <div className={styles.detailError}>{error}</div>}

          {detail && (
            <>
              {detail.context && Object.keys(detail.context).length > 0 && (
                <Section title="Context">
                  <pre className={styles.codeBlock}>
                    {formatValue(detail.context)}
                  </pre>
                </Section>
              )}

              <Section title="Prompt" defaultExpanded>
                {detail.prompt ? (
                  <pre className={styles.codeBlock}>{detail.prompt}</pre>
                ) : (
                  <span className={styles.muted}>—</span>
                )}
              </Section>

              <Section title="Thinking">
                {detail.thinking ? (
                  <pre className={styles.codeBlock}>{detail.thinking}</pre>
                ) : (
                  <span className={styles.muted}>
                    No assistant narration recorded.
                  </span>
                )}
              </Section>

              <Section title={`Tools Used (${detail.tools.length})`}>
                {detail.tools.length === 0 ? (
                  <span className={styles.muted}>No tool calls.</span>
                ) : (
                  <div className={styles.toolList}>
                    {detail.tools.map((tool, idx) => (
                      <div
                        key={`${tool.name ?? 'tool'}-${idx}`}
                        className={styles.toolCard}
                      >
                        <div className={styles.toolHeader}>
                          <span className={styles.toolName}>{tool.name}</span>
                          <ToolOutcomeTag success={tool.success} />
                          {tool.durationMs !== null && (
                            <span className={styles.toolMeta}>
                              {tool.durationMs} ms
                            </span>
                          )}
                        </div>
                        {tool.input !== null && (
                          <pre className={styles.codeBlockSmall}>
                            {formatValue(tool.input)}
                          </pre>
                        )}
                        {tool.error && (
                          <div className={styles.detailError}>{tool.error}</div>
                        )}
                        {tool.output !== null && (
                          <pre className={styles.codeBlockSmall}>
                            {formatValue(tool.output)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Output" defaultExpanded>
                {detail.output ? (
                  <pre className={styles.codeBlock}>{detail.output}</pre>
                ) : (
                  <span className={styles.muted}>No final response.</span>
                )}
              </Section>
            </>
          )}
        </div>
      )}
      {/* Carbon SidePanel requires non-null children even when closed/empty. */}
      {!run && <span aria-hidden />}
    </SidePanel>
  );
};
